import { defineStore } from "pinia";
import { doc, getDoc } from "firebase/firestore";

const COUNTY_DEFINITIONS = [
  { name: "Clark", state: "OH" },
  { name: "Clinton", state: "OH" },
  { name: "Fayette", state: "OH" },
  { name: "Greene", state: "OH" },
  { name: "Madison", state: "OH" },
  { name: "Montgomery", state: "OH" },
  { name: "Warren", state: "OH" },
];

const COUNTY_STATE_BY_COUNTY = Object.fromEntries(
  COUNTY_DEFINITIONS.map(({ name, state }) => [name, state])
);

export const useCountyStore = defineStore("county", {
  state: () => {
    return {
      counties: COUNTY_DEFINITIONS.map(({ name }) => name),
      countyStateByCounty: COUNTY_STATE_BY_COUNTY,
      county: "Greene",
      precincts: [],
      precinctGroups: [],
      docPromise: null,
    };
  },

  getters: {
    countyState(state) {
      return state.countyStateByCounty[state.county] || "OH";
    },
    getCountyState: state => county => {
      return state.countyStateByCounty[county] || "OH";
    },
  },

  actions: {
    setCounty(county) {
      this.county = county;
      // Resolve the data store lazily to avoid circular store initialization.
      const dataStore = this.$pinia?._s?.get("data");
      if (dataStore?.setCounty) {
        dataStore.setCounty(county);
      }
      this.initializeData();
    },
    async initializeData() {
      if (this.docPromise) {
        console.debug("Already fetching precincts, returning.");
        return;
      }

      const countyDocRef = this.countyDocRef();
      if (countyDocRef == null) return;
      console.debug(`Retrieving ${countyDocRef.path} document.`);

      try {
        this.docPromise = getDoc(countyDocRef);
      } catch (error) {
        console.error(`Error Accessing ${countyDocRef.path}. Error:`, error);
      }
      this.docPromise
        .then(doc => {
          const data = doc.data();
          this.precincts = data?.precincts || [];
          this.precinctGroups = data?.groups || [];
          console.debug(
            `Found ${this.precincts.length} precincts and ${this.precinctGroups.length} precinct groups.`
          );

          const unused = this.unusedPrecincts();
          if (unused.length) {
            console.debug(`${unused.length} unused precincts`);
            this.precinctGroups.push({ name: "Ungrouped", precincts: unused });
          } else {
            console.debug("All precincts are in a group.");
          }
        })
        .catch(error => {
          console.error("Error fetching precincts:", error);
        })
        .finally(() => {
          this.docPromise = null;
          console.debug("Precinct Data fetch .finally done.");
        });
    },

    /**
     * Finds precincts that are not in any group.
     * @returns {array} An array of precinct objects that are not in any group.
     */
    unusedPrecincts() {
      console.debug("Handling unused precincts.");
      let precincts = {};
      this.precincts.forEach(p => {
        precincts[p.PRECINCT_NAME] = p;
      });
      Object.values(this.precinctGroups).forEach(grp => {
        grp.precincts.forEach(p => {
          delete precincts[p.PRECINCT_NAME];
        });
      });
      precincts = Array.from(Object.values(precincts));
      precincts.sort((a, b) => a.PRECINCT_NAME.localeCompare(b.PRECINCT_NAME));
      console.debug("Done handling unused precincts.");
      return precincts;
    },
    countyDocRef() {
      if (this.county === null) {
        return null;
      }
      return doc(window.db, "precinctsByCounty", this.county);
    },
    normalizePrecinctName(fragment) {
      if (fragment === null || fragment === undefined) return null;

      const input = String(fragment).trim();
      if (!input) return input;

      const needle = input.toUpperCase();
      const matches = this.precincts
        .map(p => String(p?.PRECINCT_NAME || "").trim())
        .filter(Boolean)
        .filter(name => {
          const normalized = name.toUpperCase();
          return normalized.includes(needle) || needle.includes(normalized);
        });

      const uniqueMatches = [...new Set(matches)];
      if (uniqueMatches.length !== 1) {
        return input;
      }

      return String(uniqueMatches[0])
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    },
  },
});
